import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Star, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const REASONS = ["Trop cher", "Je ne l'utilise pas assez", "Fonctionnalités manquantes", "Je change d'outil", "Activité en pause / saisonnière", "Problèmes techniques", "Autre"];

export default function CancelFeedbackDialog({ open, onOpenChange, onCanceled }) {
  const [reason, setReason] = useState("");
  const [rating, setRating] = useState(0);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post("/revolut/cancel");
      try { await api.post("/feedback", { kind: "cancellation", reason, rating: rating || null, message }); } catch { /* feedback best-effort */ }
      toast.success("Abonnement résilié. Merci pour votre retour !");
      onOpenChange(false);
      setReason(""); setRating(0); setMessage("");
      onCanceled && onCanceled();
    } catch (e) { toast.error(e.response?.data?.detail || "Résiliation impossible"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="border-zinc-800 bg-zinc-950" data-testid="cancel-feedback-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-white"><AlertTriangle className="h-5 w-5 text-[#FF6A00]" />Résilier votre abonnement</DialogTitle>
          <DialogDescription className="text-zinc-400">Votre abonnement sera résilié. Aidez-nous à nous améliorer en partageant la raison de votre départ (facultatif).</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div>
            <Label className="text-zinc-400">Motif principal</Label>
            <Select value={reason} onValueChange={setReason}>
              <SelectTrigger data-testid="feedback-reason" className="mt-1 border-zinc-800 bg-zinc-900"><SelectValue placeholder="Choisir un motif…" /></SelectTrigger>
              <SelectContent className="border-zinc-800 bg-zinc-900">{REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-zinc-400">Votre satisfaction</Label>
            <div className="mt-1.5 flex gap-1" data-testid="feedback-rating">
              {[1, 2, 3, 4, 5].map((n) => (
                <button key={n} type="button" data-testid={`feedback-star-${n}`} onClick={() => setRating(n)} className="transition-transform hover:scale-110">
                  <Star className={`h-6 w-6 ${n <= rating ? "fill-[#FF6A00] text-[#FF6A00]" : "text-zinc-600"}`} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-zinc-400">Un message pour l'équipe ?</Label>
            <Textarea data-testid="feedback-message" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} className="mt-1 border-zinc-800 bg-zinc-900" placeholder="Ce qui vous a manqué, ce qu'on pourrait améliorer…" />
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy} className="rounded-full border-zinc-700 text-zinc-200 hover:bg-zinc-800">Annuler</Button>
            <Button data-testid="feedback-confirm" onClick={submit} disabled={busy} className="rounded-full bg-red-600 font-semibold text-white hover:bg-red-500">{busy ? "Résiliation…" : "Confirmer la résiliation"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
